import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { MnaApiError, useBrief } from '../lib/api';
import type { BriefCitationDto, BriefThemeDto } from '../types';
import { fmtDate } from './_shared';
import { WindowTabs, type BriefWindow } from './WindowTabs';

// Tab — Brief (Phase C). The latest weekly radar brief: an Opus cross-theme synthesis
// followed by the five themes as cards (HMHWatch DailyBrief pattern, citation-validated
// server-side). Inline [C1] markers in the prose render as superscript chips that carry
// the citation label as tooltip and link out — to the source document when the citation
// has a url, otherwise to the company dossier via companySlug. The overall section has
// no citation list of its own; markers resolve against the union of theme citations.

export default function Brief() {
  const [briefWindow, setBriefWindow] = useState<BriefWindow>('7d');
  const { data: brief, isLoading, error } = useBrief(briefWindow);

  // Marker → citation across all themes (the overall prose reuses theme citations).
  const citationsByMarker = useMemo(() => {
    const map = new Map<string, BriefCitationDto>();
    for (const theme of brief?.themes ?? []) {
      for (const c of theme.citations) map.set(c.marker, c);
    }
    return map;
  }, [brief]);

  if (isLoading) return <LoadingSkeleton lines={8} />;

  // 404 = no brief generated yet — a valid empty state, not an error.
  const noBriefYet = error instanceof MnaApiError && error.status === 404;
  if (error && !noBriefYet) return <ErrorState message={(error as Error).message} />;

  if (noBriefYet || !brief) {
    return (
      <Card>
        <CardTitle>Weekly radar brief</CardTitle>
        <CardDescription className="mt-2">
          First brief generates daily at 06:53 UTC — or trigger mna-brief from Debug.
        </CardDescription>
      </Card>
    );
  }

  return (
    <div className="stagger">
      <Card>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <CardTitle>Radar brief</CardTitle>
          <span className="font-(family-name:--font-ui) text-xs uppercase tracking-[0.12em] text-muted [font-variant-numeric:tabular-nums]">
            Week of {fmtDate(brief.weekOf)}
          </span>
          <span className="font-(family-name:--font-ui) text-xs text-muted [font-variant-numeric:tabular-nums]">
            generated {brief.generatedAt.replace('T', ' ').slice(0, 16)} UTC
          </span>
        </div>
        <CardDescription className="mt-2">
          Opus synthesis across the selected window's signals — every claim cites the event,
          company or deal behind it. Regenerates daily at 06:53 UTC.
        </CardDescription>
        <div className="mt-3">
          <WindowTabs value={briefWindow} onChange={setBriefWindow} />
        </div>
      </Card>

      {/* Overall synthesis */}
      <Card>
        <span className="eyebrow">Overall</span>
        <div className="mt-1 text-sm leading-relaxed text-text">
          <CitedProse text={brief.overall} citationsByMarker={citationsByMarker} />
        </div>
      </Card>

      {/* Theme cards */}
      <div className="grid gap-6 lg:grid-cols-2">
        {brief.themes.map((theme) => (
          <ThemeCard key={theme.key} theme={theme} citationsByMarker={citationsByMarker} />
        ))}
      </div>
    </div>
  );
}

/* ---------- pieces ---------- */

function ThemeCard({
  theme,
  citationsByMarker,
}: {
  theme: BriefThemeDto;
  citationsByMarker: Map<string, BriefCitationDto>;
}) {
  return (
    <Card className="mb-0">
      <span className="eyebrow">{theme.label}</span>
      <div className="mt-1 text-sm leading-relaxed text-text">
        <CitedProse text={theme.summary} citationsByMarker={citationsByMarker} />
      </div>
    </Card>
  );
}

/**
 * Structured prose renderer: blank lines split paragraphs, contiguous "- " lines render
 * as bullet lists (the LLM emits both; a single <p> flattened them into the wall of text
 * the 2026-07-06 feedback called out). Within each block, [C1]-style markers become
 * superscript citation chips and inline markdown emphasis renders.
 */
function CitedProse({
  text,
  citationsByMarker,
}: {
  text: string;
  citationsByMarker: Map<string, BriefCitationDto>;
}) {
  const blocks = text
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
        const isList = lines.length > 0 && lines.every((l) => /^[-•*]\s/.test(l));
        if (isList) {
          return (
            <ul key={bi} className="mt-3 flex list-disc flex-col gap-1.5 pl-5 first:mt-2">
              {lines.map((l, li) => (
                <li key={li}>
                  <CitedLine text={l.replace(/^[-•*]\s+/, '')} citationsByMarker={citationsByMarker} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className="mt-3 first:mt-2">
            <CitedLine text={lines.join(' ')} citationsByMarker={citationsByMarker} />
          </p>
        );
      })}
    </>
  );
}

/** One block of prose: marker-split into text runs (inline markdown) and citation chips. */
function CitedLine({
  text,
  citationsByMarker,
}: {
  text: string;
  citationsByMarker: Map<string, BriefCitationDto>;
}) {
  const parts = text.split(/\[(C\d+)\]/g);
  return (
    <>
      {parts.map((part, i) => {
        // Odd indices are the captured marker groups.
        if (i % 2 === 0) return <InlineMd key={i} text={part} />;
        const citation = citationsByMarker.get(part);
        return <CitationChip key={i} marker={part} citation={citation} />;
      })}
    </>
  );
}

/**
 * Minimal inline-markdown for LLM prose: **bold** → strong, *italic* → em. The brief
 * text arrives with markdown emphasis (Opus/Sonnet habit) — rendering it beats asking
 * the model to stop (validated text is already stored) and needs no markdown dependency.
 */
function InlineMd({ text }: { text: string }) {
  const nodes: ReactNode[] = [];
  const boldParts = text.split(/\*\*([^*]+)\*\*/g);
  boldParts.forEach((part, i) => {
    if (i % 2 === 1) {
      nodes.push(<strong key={`b${i}`}>{part}</strong>);
      return;
    }
    const emParts = part.split(/\*([^*\s][^*]*)\*/g);
    emParts.forEach((ep, j) => {
      if (j % 2 === 1) nodes.push(<em key={`e${i}-${j}`}>{ep}</em>);
      else if (ep) nodes.push(<span key={`t${i}-${j}`}>{ep}</span>);
    });
  });
  return <>{nodes}</>;
}

// Background chip, NOT a bordered one: at superscript size a border cuts straight
// through the glyphs and reads as strikethrough (the 2026-07-05 brief-tab bug).
const chipClass =
  'inline-block rounded-(--radius-sm) bg-surface-elevated px-1 leading-snug ' +
  'font-(family-name:--font-ui) text-[0.62rem] font-semibold tracking-[0.06em] ' +
  'text-accent no-underline hover:underline ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

function CitationChip({ marker, citation }: { marker: string; citation?: BriefCitationDto }) {
  // Unresolvable marker (should not happen — citations are validated server-side).
  if (!citation) {
    return (
      <sup className="ml-0.5">
        <span className="text-muted">[{marker}]</span>
      </sup>
    );
  }
  return (
    <sup className="ml-0.5">
      {citation.url ? (
        <a
          href={citation.url}
          target="_blank"
          rel="noreferrer"
          title={citation.label}
          aria-label={`Citation ${citation.marker}: ${citation.label}`}
          className={chipClass}
        >
          {citation.marker}
        </a>
      ) : citation.companySlug ? (
        <Link
          to={`/mna/companies/${citation.companySlug}`}
          title={citation.label}
          aria-label={`Citation ${citation.marker}: ${citation.label}`}
          className={chipClass}
        >
          {citation.marker}
        </Link>
      ) : (
        <span title={citation.label} className={chipClass}>
          {citation.marker}
        </span>
      )}
    </sup>
  );
}
